import { useState } from 'react';
import { NavBar } from '../components/NavBar.tsx';
import { Account } from './home/Account.tsx';
import { InstallHint } from './home/InstallHint.tsx';
import { Sheet } from '../components/Overlay.tsx';
import { ErrorState, Loading, MockBanner } from '../components/DataState.tsx';
import { useNotes } from '../api/notes.ts';
import { useMe } from '../api/me.ts';
import { roomNotes } from '../mock.ts';
import type { PrayerNote } from '../domain.ts';
import { daysUntil, ddayLabel, statusLabel } from '../domain.ts';

/**
 * B1 — Home 대쉬보드.
 *
 * 기획서 3.3 이 정한 네 가지를 각각 카드 하나로 그립니다:
 *   긴급 → 다가오는 → 최신 → 특정 모임
 * 카드 안은 3개까지만 보이고, 넘으면 해당 탭으로 넘깁니다.
 */

/** 'Plan 날짜 D-14 이내' 를 판정합니다. Everyday 노트는 대상이 아닙니다. */
const UPCOMING_WINDOW_DAYS = 14;

function upcomingDate(note: PrayerNote): string | null {
  if (note.plan.type !== 'DATE') return null;
  const d = daysUntil(note.plan.date);
  return d >= 0 && d <= UPCOMING_WINDOW_DAYS ? note.plan.date : null;
}

function DashItem({ note }: { note: PrayerNote }) {
  const date = note.plan.type === 'DATE' ? note.plan.date : null;
  const sub = [note.category, statusText(note)].join(' · ');

  return (
    <button type="button" className="dash__item" data-priority={note.priority}>
      <span className="dash__bar" />
      <span className="dash__text">
        <b>{note.title}</b>
        <span>{sub}</span>
      </span>
      {date && (
        <span className="dday" data-priority={note.priority}>
          {ddayLabel(date)}
        </span>
      )}
    </button>
  );
}

/** Everyday 노트는 날짜가 없으므로 상태 대신 Everyday 를 보여 줍니다. */
function statusText(note: PrayerNote): string {
  return note.plan.type === 'EVERYDAY' ? 'Everyday' : statusLabel[note.status];
}

function Dashboard({
  title,
  count,
  items,
}: {
  title: string;
  count: string;
  items: PrayerNote[];
}) {
  if (items.length === 0) return null;
  return (
    <section className="dash">
      <div className="dash__head">
        <b>{title}</b>
        <span className="dash__count">{count}</span>
      </div>
      {items.slice(0, 3).map((note) => (
        <DashItem key={note.id} note={note} />
      ))}
    </section>
  );
}

/**
 * B2 대쉬보드 구성.
 * 기획서 3.3 "우선 고정값만 선택 가능하게 만들고 추후 업데이트" 를 지켜
 * 켜기/끄기 + 순서까지만 열어 뒀습니다. 카드 신규 생성은 조건 편집 UI 가
 * 필요해서 1차 범위에서 뺐습니다 (design/screens.md 확인 필요 8번).
 */
const DASHBOARDS = [
  { key: 'urgent', label: '긴급 기도 노트', cond: '중요도 = 긴급', on: true },
  { key: 'upcoming', label: '다가오는 기도 노트', cond: 'Plan 날짜 D-14 이내', on: true },
  { key: 'recent', label: '최신 기도 노트', cond: '최근 작성·수정 순', on: true },
  { key: 'room', label: '특정 모임의 기도 노트', cond: '청년 3순', on: true },
  { key: 'answered', label: '응답된 기도', cond: '상태 = 응답완료', on: false },
] as const;

export function Home() {
  const [filterOpen, setFilterOpen] = useState(false);
  const [enabled, setEnabled] = useState<Record<string, boolean>>(
    Object.fromEntries(DASHBOARDS.map((d) => [d.key, d.on])),
  );
  const { notes, source, loading, error, reload } = useNotes();
  const { name } = useMe();

  const urgent = notes.filter((n) => n.priority === 'URGENT');

  const upcoming = notes
    .filter((n) => n.priority !== 'URGENT' && upcomingDate(n) !== null)
    .sort((a, b) => (upcomingDate(a) ?? '').localeCompare(upcomingDate(b) ?? ''));

  const recent = [...notes].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));

  // 오늘 기도할 제목 = 오늘 날짜인 것 + Everyday 인 것
  const todayCount = notes.filter(
    (n) => n.plan.type === 'EVERYDAY' || (n.plan.type === 'DATE' && daysUntil(n.plan.date) === 0),
  ).length;

  const today = new Date();
  const dateLine = today.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    weekday: 'long',
  });

  return (
    <div className="screen">
      <NavBar
        title="Home"
        large
        end={{
          icon: 'filter',
          label: '대쉬보드 구성',
          accent: true,
          onClick: () => setFilterOpen(true),
        }}
      />

      <div className="screen__body">
        <MockBanner />
        <InstallHint />

        {loading && <Loading label="기도 노트를 불러오는 중" />}
        {error && <ErrorState message={error} onRetry={reload} />}

        {!loading && !error && (
          <>
        <div className="today">
          <p className="today__date">{dateLine}</p>
          <p className="today__line">
            오늘 기도할 제목 <em>{todayCount}</em>
          </p>
        </div>

        {enabled.urgent && (
          <Dashboard title="긴급 기도 노트" count={String(urgent.length)} items={urgent} />
        )}
        {enabled.upcoming && (
          <Dashboard title="다가오는 기도 노트" count={String(upcoming.length)} items={upcoming} />
        )}
        {enabled.recent && (
          <Dashboard title="최신 기도 노트" count={String(recent.length)} items={recent} />
        )}

        {/* 모임 카드는 아직 백엔드가 없습니다. 로그인한 상태에서 예시 모임을
            띄우면 진짜 데이터로 오해하게 되므로, 목업 모드에서만 보여 줍니다. */}
        {enabled.room &&
          source === 'mock' &&
          roomNotes.map((rn) => (
            <section className="dash" key={rn.id}>
              <div className="dash__head">
                <b>{rn.roomName}</b>
                <span className="dash__count">모임</span>
              </div>
              <button type="button" className="dash__item" data-priority="NORMAL">
                <span className="dash__bar" />
                <span className="dash__text">
                  <b>{rn.title}</b>
                  <span>{rn.meta}</span>
                </span>
              </button>
            </section>
          ))}

        {enabled.answered && (
          <Dashboard
            title="응답된 기도"
            count={String(notes.filter((n) => n.status === 'ANSWERED').length)}
            items={notes.filter((n) => n.status === 'ANSWERED')}
          />
        )}

        {notes.length === 0 && (
          <div className="empty">
            <p className="empty__title">아직 기도 노트가 없습니다</p>
            <p className="empty__hint">
              Prayer Book 탭에서 첫 기도 노트를 만들어 보세요. 만든 노트가 여기 모입니다.
            </p>
          </div>
        )}
          </>
        )}

        {/* 맨 아래 — 자주 누를 것이 아니라 목록 뒤에 둡니다 */}
        <Account name={name} />
      </div>

      {filterOpen && (
        <Sheet
          title="대쉬보드 구성"
          action={{ label: '완료', onClick: () => setFilterOpen(false) }}
          onClose={() => setFilterOpen(false)}
        >
          <p className="sheet__hint">보고 싶은 것만 켜서 순서대로 쌓입니다</p>
          {DASHBOARDS.map((d) => (
            <button
              key={d.key}
              type="button"
              className={enabled[d.key] ? 'mrow mrow--on' : 'mrow'}
              aria-pressed={enabled[d.key]}
              onClick={() => setEnabled((prev) => ({ ...prev, [d.key]: !prev[d.key] }))}
            >
              <span className="mrow__grow">
                {d.label}
                <span className="mrow__sub">{d.cond}</span>
              </span>
              <span className={enabled[d.key] ? 'toggle toggle--on' : 'toggle'} />
            </button>
          ))}
        </Sheet>
      )}
    </div>
  );
}
